import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';
import {
  defaultProductFilter,
  productInputLabel,
  productOptionLabel,
  skuInputLabel,
  skuOptionLabel,
  filterProductOptions,
  filterSkuOptions,
} from '../private/productSkuPickerUtils';
import {
  fetchProductsCached,
  fetchSkusForProductCached,
  fetchSkuCached,
  primeSku,
} from '../private/productSkuPickerCache';

const compactInputSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: { xs: '44px', md: '40px' },
    height: { xs: '44px', md: '40px' },
    maxHeight: { xs: '44px', md: '40px' },
    fontSize: { xs: '16px', md: '0.8125rem' },
    borderRadius: '6px',
    py: '0 !important',
  },
  '& .MuiAutocomplete-inputRoot': {
    py: '0 !important',
    flexWrap: 'nowrap',
  },
  '& .MuiOutlinedInput-input': {
    py: { xs: '8px', md: '8px' },
    px: { xs: '11px', md: '10px' },
    boxSizing: 'border-box',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  '& .MuiAutocomplete-endAdornmentRoot': {
    top: '50%',
    transform: 'translateY(-50%)',
    right: 2,
  },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    padding: 2,
  },
};

const compactOutlinedRoot = compactInputSx['& .MuiOutlinedInput-root'];

const leftFieldSx = {
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: '52%',
  ...compactInputSx,
  '& .MuiOutlinedInput-root': {
    ...compactOutlinedRoot,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
};

const rightFieldSx = {
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: '48%',
  ...compactInputSx,
  '& .MuiOutlinedInput-root': {
    ...compactOutlinedRoot,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    marginLeft: '-1px',
  },
};

function buildProductStub(seed) {
  if (!seed) return null;
  const code = String(seed.code ?? '').trim();
  const name = String(seed.name ?? '').trim();
  const id = String(seed.id ?? '').trim();
  if (!code && !name && !id) return null;
  return { id: id || undefined, code, name };
}

function buildSkuStub(skuId, seed) {
  if (!skuId && !seed) return null;
  const id = String(seed?.id ?? skuId ?? '').trim();
  if (!id) return null;
  return {
    id,
    code: String(seed?.code ?? '').trim() || undefined,
    attributes: seed?.attributes,
    product_id: seed?.product_id,
  };
}

function canDisplayFromCatalogSeed(skuId, catalogSeed) {
  if (!skuId || !catalogSeed) return false;
  const product = buildProductStub(catalogSeed.product);
  const sku = buildSkuStub(skuId, catalogSeed.sku);
  if (!product || !sku) return false;
  return !!(product.code || product.name) && !!(sku.code || sku.id);
}

export function ProductSkuPicker({
  skuId = null,
  catalogSeed = null,
  onChange,
  disabled = false,
  onBlur,
  productFilter,
  size = 'small',
  sx,
}) {
  const { translate } = useTranslation();

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const productsLoadedRef = useRef(false);

  const [skusCache, setSkusCache] = useState(() => new Map());
  const skusCacheRef = useRef(new Map());
  const [skusLoading, setSkusLoading] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSku, setSelectedSku] = useState(null);
  const [productOpen, setProductOpen] = useState(false);
  const [skuOpen, setSkuOpen] = useState(false);
  const [productInputValue, setProductInputValue] = useState('');
  const [skuInputValue, setSkuInputValue] = useState('');

  const skuInputRef = useRef(null);
  const skipHydrationClearRef = useRef(false);
  const clearedSkuInternallyRef = useRef(false);
  const pendingSkuFocusRef = useRef(false);
  const pendingProductIdRef = useRef(null);
  const hydratedSkuIdRef = useRef(null);
  const catalogSeedAppliedRef = useRef(false);

  const resolvedProductFilter = productFilter || defaultProductFilter;

  const seedProductCode = catalogSeed?.product?.code ?? '';
  const seedProductName = catalogSeed?.product?.name ?? '';
  const seedSkuCode = catalogSeed?.sku?.code ?? '';

  const loadProducts = useCallback(async () => {
    if (productsLoadedRef.current && products.length > 0) return products;
    setProductsLoading(true);
    try {
      const list = await fetchProductsCached();
      productsLoadedRef.current = true;
      setProducts(list);
      return list;
    } finally {
      setProductsLoading(false);
    }
  }, [products.length]);

  const loadSkusForProduct = useCallback(async (productId) => {
    if (!productId) return [];
    if (skusCacheRef.current.has(productId)) return skusCacheRef.current.get(productId);
    setSkusLoading(true);
    try {
      const list = await fetchSkusForProductCached(productId);
      skusCacheRef.current = new Map(skusCacheRef.current).set(productId, list);
      setSkusCache(skusCacheRef.current);
      return list;
    } finally {
      setSkusLoading(false);
    }
  }, []);

  const resolveProductFromCatalog = useCallback(
    async (stub) => {
      if (!stub) return null;
      if (stub.id) {
        const list = await loadProducts();
        const byId = list.find((p) => p.id === stub.id);
        if (byId) return byId;
      }
      const code = String(stub.code ?? '').trim();
      if (!code) return stub;
      const list = await loadProducts();
      return list.find((p) => p.code === code) || stub;
    },
    [loadProducts],
  );

  const focusSkuField = useCallback(() => {
    setSkuInputValue('');
    setSkuOpen(true);
    setTimeout(() => {
      skuInputRef.current?.focus();
      setSkuOpen(true);
    }, 0);
  }, []);

  const syncProductInputFromSelection = useCallback(() => {
    setProductInputValue(selectedProduct ? productInputLabel(selectedProduct) : '');
  }, [selectedProduct]);

  const syncSkuInputFromSelection = useCallback(() => {
    setSkuInputValue(selectedSku ? skuInputLabel(selectedSku) : '');
  }, [selectedSku]);

  const applyCatalogSeedDisplay = useCallback(() => {
    if (!canDisplayFromCatalogSeed(skuId, catalogSeed)) return false;
    const productStub = buildProductStub(catalogSeed.product);
    const skuStub = buildSkuStub(skuId, catalogSeed.sku);
    setSelectedProduct(productStub);
    setSelectedSku(skuStub);
    primeSku(skuStub);
    if (!productOpen) setProductInputValue(productInputLabel(productStub));
    if (!skuOpen) setSkuInputValue(skuInputLabel(skuStub));
    hydratedSkuIdRef.current = skuId;
    catalogSeedAppliedRef.current = true;
    return true;
  }, [skuId, catalogSeed, productOpen, skuOpen]);

  useEffect(() => {
    if (!productOpen) syncProductInputFromSelection();
  }, [productOpen, syncProductInputFromSelection]);

  useEffect(() => {
    if (!skuOpen) syncSkuInputFromSelection();
  }, [skuOpen, syncSkuInputFromSelection]);

  useEffect(() => {
    if (!selectedProduct || !selectedSku) return;
    const skuProductId = selectedSku.product_id;
    if (skuProductId && selectedProduct.id && skuProductId !== selectedProduct.id) {
      setSelectedSku(null);
      setSkuInputValue('');
    }
  }, [selectedProduct, selectedSku]);

  useEffect(() => {
    if (!skuId) {
      hydratedSkuIdRef.current = null;
      catalogSeedAppliedRef.current = false;
      if (clearedSkuInternallyRef.current) {
        clearedSkuInternallyRef.current = false;
        pendingProductIdRef.current = null;
        return;
      }
      if (!skipHydrationClearRef.current) {
        setSelectedProduct(null);
        setSelectedSku(null);
        setProductInputValue('');
        setSkuInputValue('');
        setSkuOpen(false);
      }
      skipHydrationClearRef.current = false;
      pendingProductIdRef.current = null;
      return;
    }

    if (pendingProductIdRef.current != null) return;
    if (selectedSku?.id === skuId && hydratedSkuIdRef.current === skuId) return;

    if (applyCatalogSeedDisplay()) return;

    if (hydratedSkuIdRef.current === skuId && catalogSeedAppliedRef.current) return;

    let cancelled = false;
    skipHydrationClearRef.current = true;
    (async () => {
      try {
        const sku = await fetchSkuCached(skuId);
        if (cancelled || !sku) return;

        const productId = sku.product_id;
        if (selectedProduct?.id && productId !== selectedProduct.id) return;

        const productsList = await fetchProductsCached();
        if (cancelled) return;
        productsLoadedRef.current = true;
        setProducts(productsList);

        const product = productsList.find((p) => p.id === productId);
        if (!product) return;

        const skusList = await fetchSkusForProductCached(productId);
        if (cancelled) return;
        skusCacheRef.current = new Map(skusCacheRef.current).set(productId, skusList);
        setSkusCache(skusCacheRef.current);

        const matchedSku = skusList.find((s) => s.id === skuId) || sku;
        setSelectedProduct(product);
        setSelectedSku(matchedSku);
        hydratedSkuIdRef.current = skuId;
        catalogSeedAppliedRef.current = false;
        if (!productOpen) setProductInputValue(productInputLabel(product));
        if (!skuOpen) setSkuInputValue(skuInputLabel(matchedSku));
      } catch (_) {
        // silently ignore hydration errors
      } finally {
        if (!cancelled) skipHydrationClearRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuId, seedProductCode, seedProductName, seedSkuCode]);

  const productOptions = useMemo(
    () => products.filter(resolvedProductFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products],
  );

  const skuOptions = useMemo(() => {
    if (!selectedProduct?.id) return [];
    const list = skusCache.get(selectedProduct.id) || [];
    return list.filter((s) => (s.status || 'ACTIVE') === 'ACTIVE');
  }, [skusCache, selectedProduct]);

  useEffect(() => {
    if (!pendingSkuFocusRef.current || !selectedProduct || skusLoading) return;
    pendingSkuFocusRef.current = false;
    focusSkuField();
  }, [selectedProduct, skusLoading, skuOptions, focusSkuField]);

  const handleProductChange = useCallback(
    async (_, product, reason) => {
      const prevProductId = selectedProduct?.id ?? null;
      const nextProductId = product?.id ?? null;
      const productChanged = nextProductId !== prevProductId;

      setSelectedProduct(product);
      catalogSeedAppliedRef.current = false;

      if (!product) {
        pendingProductIdRef.current = null;
        setSelectedSku(null);
        setSkuInputValue('');
        setSkuOpen(false);
        setProductInputValue('');
        onChange?.({ product: null, sku: null });
        return;
      }

      if (productChanged) {
        pendingProductIdRef.current = nextProductId;
        setSelectedSku(null);
        setSkuInputValue('');
        setSkuOpen(false);
        clearedSkuInternallyRef.current = true;
        onChange?.({ product, sku: null });
      }

      if (!productOpen) {
        setProductInputValue(productInputLabel(product));
      }

      if (product.id) {
        await loadSkusForProduct(product.id);
      }

      if (productChanged && reason === 'selectOption' && !disabled) {
        pendingSkuFocusRef.current = true;
      }
    },
    [onChange, loadSkusForProduct, disabled, selectedProduct, productOpen],
  );

  const handleSkuChange = useCallback(
    (_, sku, reason) => {
      setSelectedSku(sku ?? null);
      catalogSeedAppliedRef.current = false;
      if (reason === 'selectOption') {
        setSkuOpen(false);
        setSkuInputValue(sku ? skuInputLabel(sku) : '');
      }
      if (!sku && selectedProduct) {
        clearedSkuInternallyRef.current = true;
        setSkuInputValue('');
      }
      onChange?.({ product: selectedProduct ?? null, sku: sku ?? null });
    },
    [onChange, selectedProduct],
  );

  const handleProductOpen = useCallback(() => {
    setProductOpen(true);
    setProductInputValue('');
    void (async () => {
      const list = await loadProducts();
      if (!selectedProduct?.id && selectedProduct) {
        const resolved = await resolveProductFromCatalog(selectedProduct);
        if (resolved?.id) setSelectedProduct(resolved);
      }
      if (!list.length) return;
    })();
  }, [loadProducts, selectedProduct, resolveProductFromCatalog]);

  const handleProductClose = useCallback(() => {
    setProductOpen(false);
    syncProductInputFromSelection();
  }, [syncProductInputFromSelection]);

  const handleSkuOpen = useCallback(() => {
    if (!selectedProduct) return;
    setSkuOpen(true);
    setSkuInputValue('');
    void (async () => {
      const product = await resolveProductFromCatalog(selectedProduct);
      if (product?.id) {
        if (product.id !== selectedProduct.id) setSelectedProduct(product);
        await loadSkusForProduct(product.id);
      }
    })();
  }, [selectedProduct, resolveProductFromCatalog, loadSkusForProduct]);

  const handleSkuClose = useCallback(() => {
    setSkuOpen(false);
    syncSkuInputFromSelection();
  }, [syncSkuInputFromSelection]);

  const handleProductKeyDown = useCallback(
    (e) => {
      if (e.key === 'Tab' && !e.shiftKey && selectedProduct && !disabled) {
        e.preventDefault();
        focusSkuField();
      }
    },
    [selectedProduct, disabled, focusSkuField],
  );

  return (
    <Box sx={{ display: 'flex', width: '100%', maxWidth: '100%', ...sx }}>
      <Autocomplete
        size={size}
        disabled={disabled}
        open={productOpen}
        options={productOptions}
        loading={productsLoading}
        value={selectedProduct}
        inputValue={productInputValue}
        onChange={handleProductChange}
        onOpen={handleProductOpen}
        onClose={handleProductClose}
        onInputChange={(_, newValue, reason) => {
          if (reason === 'input') {
            setProductInputValue(newValue);
          }
        }}
        filterOptions={filterProductOptions}
        getOptionLabel={productInputLabel}
        isOptionEqualToValue={(a, b) => {
          if (!a || !b) return a === b;
          if (a.id && b.id) return a.id === b.id;
          return (a.code || '') === (b.code || '') && (a.name || '') === (b.name || '');
        }}
        blurOnSelect={false}
        clearOnBlur={false}
        openOnFocus
        autoHighlight
        handleHomeEndKeys
        sx={leftFieldSx}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box component="li" key={key} {...rest}>
              <Typography variant="body2" noWrap sx={{ fontSize: '0.8125rem' }}>
                {productOptionLabel(option)}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size={size}
            placeholder={translate('products.sku_picker_product_placeholder')}
            onKeyDown={handleProductKeyDown}
            inputProps={{
              ...params.inputProps,
              'aria-label': translate('products.sku_picker_product_placeholder'),
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {productsLoading ? <CircularProgress color="inherit" size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <Autocomplete
        size={size}
        disabled={disabled || !selectedProduct}
        open={skuOpen}
        onOpen={handleSkuOpen}
        onClose={(_, reason) => {
          if (reason === 'selectOption') return;
          handleSkuClose();
        }}
        options={skuOptions}
        loading={skusLoading}
        value={selectedSku}
        inputValue={skuInputValue}
        onChange={handleSkuChange}
        onInputChange={(_, newValue, reason) => {
          if (reason === 'input') {
            setSkuInputValue(newValue);
          }
        }}
        filterOptions={filterSkuOptions}
        openOnFocus
        getOptionLabel={skuInputLabel}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        blurOnSelect
        clearOnBlur={false}
        autoHighlight
        handleHomeEndKeys
        sx={rightFieldSx}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box component="li" key={key} {...rest}>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem', whiteSpace: 'normal' }}>
                {skuOptionLabel(option)}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => {
          const { ref: autocompleteInputRef, ...skuInputProps } = params.inputProps;
          return (
          <TextField
            {...params}
            size={size}
            placeholder={
              selectedProduct
                ? translate('products.sku_picker_sku_placeholder')
                : translate('products.sku_picker_sku_disabled_placeholder')
            }
            inputProps={{
              ...skuInputProps,
              'aria-label': translate('products.sku_picker_sku_placeholder'),
              ref: (node) => {
                skuInputRef.current = node;
                if (typeof autocompleteInputRef === 'function') {
                  autocompleteInputRef(node);
                } else if (autocompleteInputRef) {
                  autocompleteInputRef.current = node;
                }
              },
              onBlur: (e) => {
                skuInputProps.onBlur?.(e);
                onBlur?.();
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {skusLoading ? <CircularProgress color="inherit" size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
          );
        }}
      />
    </Box>
  );
}
